import { ErrorHandler, Injectable, Injector, NgZone } from "@angular/core";
import { TuiAlertService } from "@taiga-ui/core";

/**
 * Global safety net for uncaught errors. Logs to console and shows a
 * non-blocking alert so leftover Zone/RxJS throws do not white-screen the shell.
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private lastAlertAt = 0;
  private readonly alertCooldownMs = 5000;

  constructor(private readonly injector: Injector) {}

  handleError(error: unknown): void {
    const message = this.extractMessage(error);

    // Router "no match" should be handled by the 404 route; ignore alert noise.
    if (message.includes("NG04002") || message.includes("Cannot match any routes")) {
      console.warn("[AppErrorHandler] unmatched route (should be covered by 404):", error);
      return;
    }

    // Browser Tracking Prevention / Google reCAPTCHA storage noise — not actionable.
    if (this.isBenignThirdPartyNoise(message)) {
      console.warn("[AppErrorHandler] ignored third-party noise:", message);
      return;
    }

    console.error("[AppErrorHandler]", error);

    const now = Date.now();
    if (now - this.lastAlertAt < this.alertCooldownMs) {
      return;
    }
    this.lastAlertAt = now;

    try {
      const zone = this.injector.get(NgZone, null);
      const alerts = this.injector.get(TuiAlertService, null);
      if (!alerts) return;

      const open = () => {
        alerts
          .open("Something went wrong. Please try again.", {
            label: "Unexpected error",
            appearance: "negative",
          })
          .subscribe({ error: () => {} });
      };

      if (zone) {
        zone.run(open);
      } else {
        open();
      }
    } catch {
      /* never let the error handler throw */
    }
  }

  private isBenignThirdPartyNoise(message: string): boolean {
    const m = message.toLowerCase();
    return (
      m.includes("requeststorageaccess") ||
      m.includes("tracking prevention") ||
      m.includes("storage access") ||
      m.includes("frame-ancestors") ||
      m.includes("recaptcha") ||
      m.includes("anchor?ar=") ||
      m.includes("networkerror when attempting to fetch resource")
    );
  }

  private extractMessage(error: unknown): string {
    if (error == null) return "";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      const err = error as { message?: unknown; rejection?: unknown };
      if (typeof err.message === "string") return err.message;
      if (err.rejection != null) return this.extractMessage(err.rejection);
      try {
        return String(error);
      } catch {
        return "";
      }
    }
    return String(error);
  }
}
